import { autoUpdate, flip, FloatingPortal, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { MenuResolution } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { RefObject, useLayoutEffect } from 'react'

import { AstraAvatar, AstraLocationChip, AstraParagraph, astraTwMerge } from '~/astra'
import { PractitionerChipWithTooltip } from '~/components/PractitionerChip'
import { getInitials } from '~/utils/user'

import { SurgeryTypeMentionNodeComponent } from './MentionNodeComponent'
import type { MentionOption } from './MentionsPlugin'
import type { MentionType } from './types'

export type GroupedMentionOptions = {
    entityType: MentionType
    label: string
    options: MentionOption[]
}

const MentionMenuItem = ({ option }: { option: MentionOption }) => {
    const mentionType = option.mention.type
    if (mentionType === 'practitioner') {
        return (
            <>
                <PractitionerChipWithTooltip size="small" shortName={option.mention.value} fullName={option.mention.fullname} />
                <AstraParagraph variant="body" weight="regular">
                    {option.mention.fullname}
                </AstraParagraph>
            </>
        )
    }
    if (mentionType === 'user') {
        return (
            <>
                <AstraAvatar size="xsmall" variant="initial" initials={getInitials(option.mention.value)} />
                <AstraParagraph variant="body" weight="regular">
                    {option.mention.value}
                </AstraParagraph>
            </>
        )
    }
    if (mentionType === 'location') {
        return <AstraLocationChip size="small" shortName={option.mention.value} variant="ghost" />
    }
    mentionType satisfies 'surgeryType'
    return <SurgeryTypeMentionNodeComponent mention={option.mention} disableHover={true} />
}

export const MentionsMenu = ({
    groupedOptions,
    onSelect,
    selectedOption,
    anchorElementRef,
    setHighlightedOption,
    resolution,
    onOutsideClick,
    rootPortalRef,
}: {
    anchorElementRef: RefObject<HTMLElement | null>
    groupedOptions: GroupedMentionOptions[]
    onSelect: (option: MentionOption) => void
    selectedOption: MentionOption | null | undefined
    setHighlightedOption: (option: MentionOption) => void
    resolution: MenuResolution
    onOutsideClick: () => void
    rootPortalRef?: RefObject<HTMLDivElement | null>
}) => {
    const { refs, context, floatingStyles } = useFloating({
        placement: 'top-start',
        strategy: 'fixed',
        open: true,
        middleware: [offset(), shift({ padding: 8 }), flip({ crossAxis: false })],
        whileElementsMounted: autoUpdate,
        onOpenChange: (open, _, reason) => {
            if (!open && reason === 'outside-press') {
                onOutsideClick()
            }
        },
    })

    const dismiss = useDismiss(context)
    const { getFloatingProps } = useInteractions([dismiss])

    useLayoutEffect(() => {
        refs.setPositionReference({
            getBoundingClientRect: resolution.getRect,
        })
    }, [refs, resolution])

    return (
        <FloatingPortal root={rootPortalRef?.current ?? anchorElementRef.current}>
            <div
                ref={refs.setFloating}
                className="relative z-priority w-max min-w-64 rounded-md border border-subtle bg-surface-base py-2 text-default shadow-lg"
                style={floatingStyles}
                {...getFloatingProps()}
            >
                <ul className="flex flex-col gap-1">
                    {groupedOptions.map((group, index) => (
                        <div
                            key={group.label}
                            className={astraTwMerge('flex flex-col gap-1 py-2', index < groupedOptions.length - 1 ? 'border-b border-subtle' : '')}
                        >
                            <AstraParagraph variant="body" weight="regular" className="px-3 text-subtle">
                                {group.label}
                            </AstraParagraph>
                            {group.options.map(option => (
                                <li
                                    ref={option.setRefElement}
                                    key={`${option.mention.type}-${option.mention.id}`}
                                    className={`flex w-full cursor-pointer items-center gap-2 px-3 py-1 text-left text-sm transition-colors ${
                                        selectedOption?.mention.id === option.mention.id && selectedOption.mention.type === option.mention.type
                                            ? 'bg-neutral-subtle-rest'
                                            : 'hover:bg-neutral-subtle-rest'
                                    }`}
                                    onClick={e => {
                                        e.preventDefault()
                                        onSelect(option)
                                    }}
                                    onMouseDown={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setHighlightedOption(option)
                                    }}
                                >
                                    <MentionMenuItem option={option} />
                                </li>
                            ))}
                        </div>
                    ))}
                </ul>
            </div>
        </FloatingPortal>
    )
}
